import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    UserCheck,
    Clock,
    CheckCircle2,
    XCircle,
    Info,
    HeartHandshake,
    UserPlus,
    Users,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useConfirm } from "../../components/ConfirmDialog";

const STATUS_FILTERS = [
    { value: "", label: "Toutes" },
    { value: "pending", label: "En attente" },
    { value: "approved", label: "Validées" },
    { value: "rejected", label: "Refusées" },
];

const STATUS_BADGE = {
    pending: { label: "En attente", cls: "bg-shariti-yellow/20 text-shariti-yellow", icon: Clock },
    approved: { label: "Validé", cls: "bg-emerald-500/20 text-emerald-400", icon: CheckCircle2 },
    rejected: { label: "Refusé", cls: "bg-red-500/20 text-red-400", icon: XCircle },
};

const KIND_BADGE = {
    self: { label: "Auto-inscription", cls: "bg-shariti-orange/20 text-shariti-orange-light", icon: UserPlus },
    self_google: { label: "Auto-inscription (Google)", cls: "bg-shariti-orange/20 text-shariti-orange-light", icon: UserPlus },
    cooptation: { label: "Cooptation", cls: "bg-sky-500/20 text-sky-400", icon: Users },
};

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (e) {
        return "—";
    }
}

export default function AdminCandidates() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [zoomPhoto, setZoomPhoto] = useState(null);
    const confirm = useConfirm();

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(
                filter ? `/admin/candidates?status=${filter}` : "/admin/candidates",
            );
            setItems(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const approve = async (c) => {
        const isCoopt = c.signup_kind === "cooptation";
        const ok = await confirm({
            title: `Valider l'inscription de ${c.name} ?`,
            description: [
                "La personne devient apprenant·e et accède à la formation.",
                c.desired_association_name
                    ? `Elle sera rattachée à l'association « ${c.desired_association_name} » (modifiable ensuite).`
                    : "",
                isCoopt && c.referrer_name
                    ? `Le recruteur ${c.referrer_name} recevra son bonus de cooptation.`
                    : "",
            ]
                .filter(Boolean)
                .join(" "),
            confirmLabel: "Valider",
        });
        if (!ok) return;
        try {
            const { data } = await api.post(`/admin/candidates/${c.user_id}/approve`);
            const parts = ["Inscription validée."];
            if (data.attached_association)
                parts.push(`Rattaché·e à ${data.attached_association}.`);
            if (data.points_paid_to_referrer > 0)
                parts.push(`+${data.points_paid_to_referrer} pts pour le parrain.`);
            toast.success(parts.join(" "));
            reload();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
        }
    };

    const reject = async (c) => {
        const reason = window.prompt(
            `Refuser ${c.name} ?\nRaison (optionnelle, communiquée au candidat) :`,
            "",
        );
        if (reason === null) return; // user cancelled
        try {
            await api.post(`/admin/candidates/${c.user_id}/reject`, {
                reason: reason || null,
            });
            toast.success("Inscription refusée");
            reload();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-candidates">
            <div>
                <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                    Auto-inscriptions & cooptations
                </p>
                <h2 className="text-2xl font-extrabold text-white flex items-center gap-2">
                    <UserCheck className="w-6 h-6 text-shariti-orange-light" />
                    Inscriptions à valider
                </h2>
                <p className="text-sm text-white/60 mt-1 flex items-start gap-2">
                    <Info className="w-3.5 h-3.5 mt-1 text-shariti-orange-light shrink-0" />
                    Vérifie la photo et l'association, puis valide en 1 clic : la personne
                    devient apprenant·e et est rattachée à son association (modifiable
                    ensuite). Les cooptations validées déclenchent le bonus du recruteur.
                </p>
            </div>

            <div className="overflow-x-auto scrollbar-hide -mx-1 px-1">
                <div className="flex gap-2 p-1.5 glass-card rounded-full w-max">
                    {STATUS_FILTERS.map((f) => (
                        <button
                            key={f.value}
                            onClick={() => setFilter(f.value)}
                            data-testid={`candidates-filter-${f.value || "all"}`}
                            className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                                filter === f.value
                                    ? "bg-shariti-orange text-white"
                                    : "text-white/60 hover:text-white"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="h-32 glass-card rounded-3xl animate-pulse" />
            ) : items.length === 0 ? (
                <div
                    className="glass-card rounded-3xl p-8 text-center"
                    data-testid="candidates-empty"
                >
                    <UserCheck className="w-10 h-10 text-white/30 mx-auto mb-3" />
                    <p className="text-white/60">Aucune inscription pour ce filtre.</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map((c) => {
                        const badge =
                            STATUS_BADGE[c.candidate_status] || STATUS_BADGE.pending;
                        const StatusIcon = badge.icon;
                        const kind = KIND_BADGE[c.signup_kind];
                        return (
                            <motion.div
                                key={c.user_id}
                                layout
                                className="glass-card rounded-2xl p-4 flex flex-wrap lg:flex-nowrap items-start lg:items-center gap-4"
                                data-testid={`candidate-row-${c.user_id}`}
                            >
                                {c.photo ? (
                                    <button
                                        type="button"
                                        onClick={() => setZoomPhoto(c.photo)}
                                        data-testid={`candidate-photo-${c.user_id}`}
                                        className="shrink-0 group relative"
                                        title="Agrandir la photo"
                                    >
                                        <img
                                            src={c.photo}
                                            alt={c.name}
                                            className="w-14 h-14 rounded-2xl object-cover border-2 border-shariti-orange/40 group-hover:border-shariti-orange transition-all"
                                        />
                                    </button>
                                ) : (
                                    <div className="w-14 h-14 rounded-2xl bg-shariti-orange/15 flex items-center justify-center shrink-0 font-extrabold text-shariti-orange-light">
                                        {c.name?.[0]}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-extrabold text-white truncate">
                                            {c.name}
                                        </p>
                                        <span
                                            className={`text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full ${badge.cls} inline-flex items-center gap-1`}
                                        >
                                            <StatusIcon className="w-3 h-3" />
                                            {badge.label}
                                        </span>
                                        {kind && (
                                            <span
                                                className={`text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full ${kind.cls} inline-flex items-center gap-1`}
                                                data-testid={`candidate-kind-${c.user_id}`}
                                            >
                                                <kind.icon className="w-3 h-3" />
                                                {kind.label}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-white/50 mt-0.5 truncate">
                                        {c.email} · inscrit le {formatDate(c.created_at)}
                                    </p>
                                    {c.desired_association_name && (
                                        <p
                                            className="text-xs text-shariti-orange-light/90 mt-0.5 truncate flex items-center gap-1"
                                            data-testid={`candidate-asso-${c.user_id}`}
                                        >
                                            <HeartHandshake className="w-3 h-3 shrink-0" />
                                            Association : {c.desired_association_name}
                                        </p>
                                    )}
                                    {c.referrer_name && (
                                        <p className="text-xs text-sky-400/80 mt-0.5 truncate">
                                            Recruteur : {c.referrer_name}
                                        </p>
                                    )}
                                    {c.candidate_status === "approved" && c.approved_by_name && (
                                        <p className="text-xs text-white/40 mt-0.5">
                                            Validé par {c.approved_by_name} le {formatDate(c.approved_at)}
                                        </p>
                                    )}
                                    {c.candidate_status === "rejected" && (
                                        <p className="text-xs text-red-400/80 mt-0.5 italic">
                                            {c.rejection_reason || "Refusé"}
                                        </p>
                                    )}
                                </div>
                                {c.candidate_status === "pending" && (
                                    <div className="flex gap-2 shrink-0 ml-auto">
                                        <button
                                            onClick={() => approve(c)}
                                            data-testid={`candidate-approve-${c.user_id}`}
                                            className="inline-flex items-center gap-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 font-extrabold text-xs uppercase tracking-widest px-3 py-2 rounded-xl transition-all"
                                        >
                                            <CheckCircle2 className="w-3.5 h-3.5" />
                                            Valider
                                        </button>
                                        <button
                                            onClick={() => reject(c)}
                                            data-testid={`candidate-reject-${c.user_id}`}
                                            className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/40 text-white/70 hover:text-red-400 font-extrabold text-xs uppercase tracking-widest px-3 py-2 rounded-xl transition-all"
                                        >
                                            <XCircle className="w-3.5 h-3.5" />
                                            Refuser
                                        </button>
                                    </div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>
            )}

            {zoomPhoto && (
                <div
                    className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 cursor-zoom-out"
                    onClick={() => setZoomPhoto(null)}
                    data-testid="candidate-photo-zoom"
                >
                    <img
                        src={zoomPhoto}
                        alt="Photo du candidat"
                        className="max-w-full max-h-[80vh] rounded-3xl border-2 border-shariti-orange/50 shadow-2xl"
                    />
                </div>
            )}
        </div>
    );
}
